// src/pages/ConfirmBooking/ConfirmBookingMobile.jsx
import React from "react";
import {
  PALETTE,
  getNiceDate,
  SectionCard,
  Label,
  DatePill,
  AcPill,
  SeatPill,
  TimeGreenPill,
  HoldCountdown,
  RowInput,
  PassengerRow,
} from "./core";

// ✅ no-op replacement so existing JSX does not change
const BookingSteps = () => null;

const ConfirmBookingMobile = ({
  // refs
  pageTopRef,

  // core values
  bus,
  date,
  departureTime,
  selectedBoardingPoint,
  selectedDroppingPoint,
  selectedSeats,
  prices,
  errors,
  holdExpired,
  termsAccepted,
  cameBackFromGateway,
  lockVersion,
  clientId,
  isSubmitting,
  showLeaveConfirm,
  closeLeaveConfirm,
  confirmLeaveToResults,

  // form state & handlers
  form,
  onChangeForm,
  blurValidateField,

  // passengers
  passengers,
  setPassengerName,
  setPassengerAge,
  setPassengerGender,
  blurValidatePassenger,

  // seat hold controls
  setHoldExpired,
  releaseSeats,

  // terms
  toggleTerms,

  // navigation / actions
  goBackToResults,
  openLeaveConfirm,
  handleSubmit,
  handleHoldExpiredRedirect,
}) => {
  const hasErrors =
    errors.name ||
    errors.mobile ||
    errors.nic ||
    errors.email ||
    Object.keys(errors.passengers || {}).length ||
    errors.terms ||
    holdExpired;

  const canPay = termsAccepted && !holdExpired && !isSubmitting;

  const boarding =
    typeof selectedBoardingPoint === "object"
      ? selectedBoardingPoint
      : { point: selectedBoardingPoint, time: "" };

  const dropping =
    typeof selectedDroppingPoint === "object"
      ? selectedDroppingPoint
      : { point: selectedDroppingPoint, time: "" };

  return (
    <div
      ref={pageTopRef}
      className="min-h-screen text-[16px]"
      style={{ background: PALETTE.bg }}
    >
      {/* Top bar */}
      <div
        className="sticky top-0 z-30 backdrop-blur-md"
        style={{
          background: "rgba(255,255,255,0.92)",
          paddingTop: "env(safe-area-inset-top)",
          borderBottom: "1px solid #E5E7EB",
          boxShadow: "0 2px 12px rgba(16,24,40,0.04)",
        }}
      >
        <div className="px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-gray-900 text-base font-bold leading-tight">
              Confirm Booking
            </p>
            <p className="text-gray-500 text-xs truncate">
              {bus?.from} → {bus?.to}
              {date ? ` • ${getNiceDate(date)}` : ""}
              {departureTime ? ` at ${departureTime}` : ""}
            </p>
          </div>

          <button
            type="button"
            onClick={openLeaveConfirm}
            className="px-3 py-1.5 rounded-full text-[11px] font-semibold shrink-0 active:scale-[0.98] transition-all duration-200"
            style={{
              background: "#F3F4F6",
              color: "#111827",
              border: "1px solid #E5E7EB",
            }}
          >
            Back
          </button>
        </div>
      </div>

      <div className="px-4 pb-32">
        <div className="pt-2">
          <BookingSteps currentStep={3} />
        </div>

        {cameBackFromGateway ? (
          <div
            className="mt-3 rounded-2xl px-4 py-3 text-xs font-medium"
            style={{
              background: "#FEF2F2",
              color: "#991B1B",
              border: "1px solid #FECACA",
            }}
          >
            Payment was cancelled or failed. You can review your details and try
            again.
          </div>
        ) : null}

        {hasErrors ? (
          <div
            className="mt-3 rounded-2xl px-4 py-3 text-xs font-medium"
            style={{
              background: "#FEF2F2",
              color: "#991B1B",
              border: "1px solid #FECACA",
            }}
          >
            {holdExpired
              ? "Your seat hold has expired. Please go back and reselect seats."
              : "Please correct the highlighted fields below."}
          </div>
        ) : null}

        {/* Journey Overview */}
        <div
          className="rounded-3xl overflow-hidden mt-4"
          style={{
            background: "#FFFFFF",
            border: `1px solid rgba(199,74,80,0.12)`,
            boxShadow:
              "0 2px 8px rgba(199,74,80,0.06), 0 12px 32px rgba(16,24,40,0.06)",
          }}
        >
          <div
            className="px-4 py-4"
            style={{
              background:
                "linear-gradient(100deg, #C74A50 0%, #D4575D 55%, #B8404A 100%)",
            }}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-white font-bold text-base leading-tight break-words">
                  {bus?.name || bus?.busName || "Bus"}
                </p>
                <p className="text-white/75 text-xs mt-0.5">
                  {bus?.busType || "AC"} · {(selectedSeats || []).length}{" "}
                  {(selectedSeats || []).length === 1 ? "Seat" : "Seats"} selected
                </p>
              </div>

              <div className="shrink-0 text-right">
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/60 mb-1">
                  Seat Hold
                </p>
                <HoldCountdown
                  key={`hold-${lockVersion}-${(selectedSeats || [])
                    .map(String)
                    .join(",")}`}
                  busId={bus?._id}
                  date={date}
                  departureTime={departureTime}
                  seats={selectedSeats}
                  clientId={clientId}
                  lockVersion={lockVersion}
                  onExpire={handleHoldExpiredRedirect}
                />
              </div>
            </div>
          </div>

          <div
            className="px-4 py-4"
            style={{ borderBottom: "1px solid #F3F4F6" }}
          >
            <div className="flex flex-wrap gap-2 mb-4">
              <DatePill>{getNiceDate(date)}</DatePill>
              <AcPill>{bus?.busType || "AC"}</AcPill>
              {(selectedSeats || []).map((s) => (
                <SeatPill key={s}>Seat {s}</SeatPill>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="min-w-0">
                <p
                  className="text-[10px] font-bold uppercase tracking-widest mb-1.5"
                  style={{ color: "#059669" }}
                >
                  Boarding
                </p>
                <p
                  className="font-bold text-sm break-words"
                  style={{ color: PALETTE.text }}
                >
                  {boarding.point || "—"}
                </p>
                {boarding.time ? (
                  <p
                    className="text-xs font-semibold mt-1 tabular-nums"
                    style={{ color: "#059669" }}
                  >
                    {boarding.time}
                  </p>
                ) : null}
              </div>

              <div className="min-w-0 text-right">
                <p
                  className="text-[10px] font-bold uppercase tracking-widest mb-1.5"
                  style={{ color: PALETTE.primary }}
                >
                  Dropping
                </p>
                <p
                  className="font-bold text-sm break-words"
                  style={{ color: PALETTE.text }}
                >
                  {dropping.point || "—"}
                </p>
                {dropping.time ? (
                  <p
                    className="text-xs font-semibold mt-1 tabular-nums"
                    style={{ color: PALETTE.primary }}
                  >
                    {dropping.time}
                  </p>
                ) : null}
              </div>
            </div>
          </div>

          <div className="px-4 py-3">
            <p
              className="text-[10px] font-bold uppercase tracking-widest mb-2"
              style={{ color: PALETTE.textMuted }}
            >
              Selected Seats
            </p>
            <div className="flex flex-wrap gap-2">
              {(selectedSeats || []).map((s) => (
                <SeatPill key={s}>Seat {s}</SeatPill>
              ))}
            </div>
          </div>
        </div>

        {/* Fare Summary */}
        <SectionCard title="Fare Summary">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between items-center gap-3">
              <span
                className="font-medium"
                style={{ color: PALETTE.textSubtle }}
              >
                Subtotal
              </span>
              <span
                className="tabular-nums font-semibold"
                style={{ color: PALETTE.text }}
              >
                Rs. {prices.basePrice.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center gap-3">
              <span
                className="font-medium"
                style={{ color: PALETTE.textSubtle }}
              >
                Convenience Fee
              </span>
              <span
                className="tabular-nums font-semibold"
                style={{ color: PALETTE.text }}
              >
                Rs. {prices.convenienceFee.toFixed(2)}
              </span>
            </div>
            <hr className="my-3" style={{ borderColor: PALETTE.border }} />
            <div className="flex justify-between items-center gap-3 text-base">
              <span className="font-bold" style={{ color: PALETTE.text }}>
                Total Payable
              </span>
              <span
                className="tabular-nums font-extrabold"
                style={{ color: PALETTE.primary }}
              >
                Rs. {prices.total.toFixed(2)}
              </span>
            </div>
            {holdExpired ? (
              <p
                className="text-xs mt-2 font-semibold"
                style={{ color: "#991B1B" }}
              >
                Your seat hold has expired. Please go back and reselect seats.
              </p>
            ) : null}
          </div>
        </SectionCard>

        {/* Contact Details */}
        <SectionCard title="Contact Details">
          <div className="grid grid-cols-1 gap-4">
            <RowInput
              id="name"
              name="name"
              label="Full Name"
              value={form.name}
              onChange={onChangeForm}
              onBlur={() => blurValidateField("name")}
              autoComplete="name"
              enterKeyHint="next"
              placeholder="e.g., Ramesh Perera"
              required
              error={errors.name}
              maxLength={80}
            />
            <RowInput
              id="mobile"
              name="mobile"
              label="Mobile Number"
              type="tel"
              value={form.mobile}
              onChange={onChangeForm}
              onBlur={() => blurValidateField("mobile")}
              autoComplete="tel"
              inputMode="tel"
              enterKeyHint="next"
              placeholder="e.g., 07XXXXXXXX"
              required
              error={errors.mobile}
              maxLength={11}
              pattern="^0\\d{9,10}$"
            />
            <RowInput
              id="nic"
              name="nic"
              label="NIC / Passport"
              value={form.nic}
              onChange={onChangeForm}
              onBlur={() => blurValidateField("nic")}
              autoComplete="off"
              enterKeyHint="next"
              placeholder="e.g., 200012345678"
              required
              error={errors.nic}
              maxLength={20}
            />
            <RowInput
              id="email"
              name="email"
              label="Email Address"
              type="email"
              value={form.email}
              onChange={onChangeForm}
              onBlur={() => blurValidateField("email")}
              autoComplete="email"
              inputMode="email"
              enterKeyHint="done"
              placeholder="e.g., ramesh@email.com"
              required
              error={errors.email}
              maxLength={100}
            />
          </div>
        </SectionCard>

        {/* Passenger Details */}
        <SectionCard title="Passenger Details">
          <div className="space-y-4">
            {passengers.map((p, idx) => {
              const seatErrors = errors.passengers?.[p.seat] || {};

              return (
                <div
                  key={p.seat}
                  className="rounded-[28px] px-4 py-4"
                  style={{
                    background: PALETTE.surface,
                    border: `1px solid ${PALETTE.border}`,
                    boxShadow: "0 10px 26px rgba(16,24,40,0.04)",
                  }}
                >
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <h3
                      className="text-[20px] font-extrabold leading-tight"
                      style={{ color: PALETTE.text, letterSpacing: "-0.025em" }}
                    >
                      Passenger {idx + 1}
                    </h3>

                    <div
                      className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[13px] font-extrabold leading-none tabular-nums"
                      style={{
                        color: "#6D5BD0",
                        background:
                          "linear-gradient(180deg, rgba(109,91,208,0.12) 0%, rgba(109,91,208,0.07) 100%)",
                        border: "1px solid rgba(109,91,208,0.22)",
                        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.75)",
                      }}
                    >
                      <span className="text-[11px] uppercase tracking-[0.16em] opacity-80">
                        Seat
                      </span>
                      <span className="text-[16px]">{p.seat}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <Label htmlFor={`passenger-name-${p.seat}`}>Name</Label>
                      <input
                        id={`passenger-name-${p.seat}`}
                        type="text"
                        value={p.name || ""}
                        onChange={(e) => setPassengerName(p.seat, e.target.value)}
                        onBlur={() => blurValidatePassenger(p.seat, "name")}
                        autoComplete="name"
                        enterKeyHint="next"
                        placeholder="e.g., Ramesh Perera"
                        maxLength={80}
                        className="w-full rounded-[22px] px-4 py-3.5 text-[16px] font-medium outline-none transition-all duration-200"
                        style={{
                          color: PALETTE.text,
                          background: PALETTE.surface,
                          border: `1.5px solid ${
                            seatErrors.name ? "#FCA5A5" : PALETTE.border
                          }`,
                          boxShadow: seatErrors.name
                            ? "0 0 0 4px rgba(239,68,68,0.08)"
                            : "none",
                        }}
                      />
                      {seatErrors.name ? (
                        <p
                          className="mt-1.5 text-xs font-medium"
                          style={{ color: "#B91C1C" }}
                        >
                          {seatErrors.name}
                        </p>
                      ) : null}
                    </div>

                    <div>
                      <Label htmlFor={`passenger-age-${p.seat}`}>Age</Label>
                      <input
                        id={`passenger-age-${p.seat}`}
                        type="number"
                        value={p.age || ""}
                        onChange={(e) => setPassengerAge(p.seat, e.target.value)}
                        onBlur={() => blurValidatePassenger(p.seat, "age")}
                        inputMode="numeric"
                        enterKeyHint="next"
                        placeholder="e.g., 28"
                        min="1"
                        max="120"
                        className="w-full rounded-[22px] px-4 py-3.5 text-[16px] font-medium outline-none transition-all duration-200"
                        style={{
                          color: PALETTE.text,
                          background: PALETTE.surface,
                          border: `1.5px solid ${
                            seatErrors.age ? "#FCA5A5" : PALETTE.border
                          }`,
                          boxShadow: seatErrors.age
                            ? "0 0 0 4px rgba(239,68,68,0.08)"
                            : "none",
                        }}
                      />
                      {seatErrors.age ? (
                        <p
                          className="mt-1.5 text-xs font-medium"
                          style={{ color: "#B91C1C" }}
                        >
                          {seatErrors.age}
                        </p>
                      ) : null}
                    </div>

                    <div>
                      <Label>Gender</Label>
                      <div className="grid grid-cols-2 gap-3">
                        {["Male", "Female"].map((gender) => {
                          const active = p.gender === gender;

                          return (
                            <button
                              key={gender}
                              type="button"
                              onClick={() => setPassengerGender(p.seat, gender)}
                              className="rounded-[22px] px-4 py-3.5 text-[16px] font-extrabold active:scale-[0.98] transition-all duration-200"
                              style={{
                                color: active ? "#6D5BD0" : PALETTE.text,
                                background: active
                                  ? "rgba(109,91,208,0.10)"
                                  : PALETTE.surface,
                                border: active
                                  ? "1.8px solid #7C66E8"
                                  : `1.5px solid ${PALETTE.border}`,
                                boxShadow: active
                                  ? "0 0 0 4px rgba(124,102,232,0.08)"
                                  : "none",
                              }}
                            >
                              {gender}
                            </button>
                          );
                        })}
                      </div>
                      {seatErrors.gender ? (
                        <p
                          className="mt-1.5 text-xs font-medium"
                          style={{ color: "#B91C1C" }}
                        >
                          {seatErrors.gender}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>

        {/* Terms */}
        <SectionCard title="Terms & Conditions">
          <p
            className="text-sm leading-6"
            style={{ color: PALETTE.textSubtle }}
          >
            Please confirm that all passenger and journey details are correct
            before proceeding to payment. By continuing, you agree to our{" "}
            <a
              href="/terms"
              target="_blank"
              rel="noreferrer"
              className="font-semibold underline underline-offset-2"
              style={{ color: PALETTE.primary }}
            >
              Terms &amp; Conditions
            </a>{" "}
            and{" "}
            <a
              href="/refund-policy"
              target="_blank"
              rel="noreferrer"
              className="font-semibold underline underline-offset-2"
              style={{ color: PALETTE.primary }}
            >
              Refund Policy
            </a>
            .
          </p>

          <label
            className="mt-4 flex items-start gap-3 rounded-2xl px-4 py-3.5 cursor-pointer transition-all duration-200"
            style={{
              background: termsAccepted
                ? PALETTE.primarySoft
                : PALETTE.surfaceAlt,
              border: `1.5px solid ${
                errors.terms
                  ? "#FECACA"
                  : termsAccepted
                  ? "rgba(199,74,80,0.25)"
                  : PALETTE.border
              }`,
              color: PALETTE.text,
            }}
          >
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 shrink-0 accent-[#C74A50]"
              checked={termsAccepted}
              onChange={toggleTerms}
              required
            />
            <span className="text-sm leading-6">
              I confirm that the entered details are correct and I agree to all
              Terms &amp; Conditions.
            </span>
          </label>

          {errors.terms ? (
            <p
              className="mt-2 text-xs font-medium"
              style={{ color: "#B91C1C" }}
            >
              {errors.terms}
            </p>
          ) : (
            <p
              className="mt-2 text-xs"
              style={{ color: PALETTE.textSubtle }}
            >
              You must accept this before proceeding to payment.
            </p>
          )}
        </SectionCard>

        {/* Spacer so content isn't hidden behind sticky bottom sheet */}
        <div className="h-28" />
      </div>

      {showLeaveConfirm ? (
        <div className="fixed inset-0 z-[120]">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
            onClick={closeLeaveConfirm}
          />
          <div
            className="absolute inset-x-0 bottom-0 px-3"
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 10px)" }}
          >
            <div
              className="w-full max-w-md mx-auto rounded-t-[30px] rounded-b-[26px] overflow-hidden shadow-2xl"
              style={{
                background: PALETTE.surface,
                border: `1px solid ${PALETTE.borderSoft}`,
                boxShadow: "0 20px 40px rgba(16,24,40,0.18)",
              }}
            >
              <div className="px-5 pt-3 pb-1">
                <div
                  className="mx-auto h-1.5 w-12 rounded-full"
                  style={{ background: "#E5E7EB" }}
                />
              </div>

              <div className="px-6 pt-4 pb-6">
                <h3
                  className="text-[20px] leading-tight font-extrabold text-center"
                  style={{ color: PALETTE.text, letterSpacing: "-0.03em" }}
                >
                  Return to search results?
                </h3>

                <p
                  className="mt-3 text-center text-[15px] leading-7"
                  style={{ color: PALETTE.textSubtle }}
                >
                  Your seat preference and details are saved
                </p>

                <div className="mt-6 flex flex-col gap-4">
                  <button
                    type="button"
                    onClick={closeLeaveConfirm}
                    className="w-full px-4 py-4 rounded-full text-[17px] font-bold text-white active:scale-[0.98] transition-all duration-200"
                    style={{
                      background: "linear-gradient(180deg, #E44B55 0%, #D63E49 100%)",
                      boxShadow: "0 8px 22px rgba(214,62,73,0.22)",
                    }}
                  >
                    Continue booking
                  </button>

                  <button
                    type="button"
                    onClick={confirmLeaveToResults}
                    className="w-full px-4 py-4 rounded-full text-[17px] font-semibold active:scale-[0.98] transition-all duration-200"
                    style={{
                      background: PALETTE.surface,
                      color: PALETTE.text,
                      border: "2px solid #1F2937",
                    }}
                  >
                    View other buses
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Mobile sticky bottom CTA */}
      {!showLeaveConfirm && (
        <div
          className="fixed left-0 right-0 bottom-0 z-[10002]"
          style={{
            paddingBottom: "calc(env(safe-area-inset-bottom))",
            background: PALETTE.surface,
            borderTop: `1px solid ${PALETTE.border}`,
            boxShadow: "0 -10px 30px rgba(16,24,40,0.06)",
          }}
        >
          <div className="px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p
                  className="text-[11px] font-semibold leading-none"
                  style={{ color: PALETTE.textSubtle }}
                >
                  Amount
                </p>
                <p
                  className="text-[11px] mt-1"
                  style={{ color: PALETTE.textMuted }}
                >
                  (Tax included)
                </p>
              </div>

              <div
                className="text-2xl font-extrabold tabular-nums leading-none"
                style={{ color: PALETTE.text }}
              >
                Rs. {prices.total.toFixed(2)}
              </div>
            </div>

            <button
              type="button"
              disabled={!canPay}
              onClick={() => {
                handleSubmit({ preventDefault: () => {} });
              }}
              className="mt-3 w-full px-4 py-3.5 rounded-full font-bold text-white shadow-md active:scale-[0.98] transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{
                background: canPay
                  ? `linear-gradient(100deg, ${PALETTE.primary} 0%, ${PALETTE.primaryDark} 100%)`
                  : "#D1D5DB",
              }}
            >
              {isSubmitting ? "Processing..." : "Continue booking"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfirmBookingMobile;





